import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { AppService } from '../../services/app.service';
import { finalize, tap } from 'rxjs/operators';
import { EmployeeCardService } from '../../services/employee-card.service';
import { EmployeeCardStatus, IEmployeeCardProps, IEmployeeCard, IEmployeeCardEditModel } from '../../models/EmployeeCard';
import { MessageService } from '../../services/message.service';
import { ParameterService } from '../../services/parameter.service';
import { Utils } from '../../core/Utils';
import { IPersonSearchResultItem } from '../../models/Person';
import { ActivatedRoute } from '@angular/router';
import { EmployeeCardRevokeComponent } from './revoke.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subscription, forkJoin } from 'rxjs';

const codes = {
    front: 'EMPLOYEE_CARD_FRONT',
    back: 'EMPLOYEE_CARD_BACK',
    requirements: 'EMPLOYEE_CARD_REQS',
    admin: 'EMPLOYEE_CARD.ADMIN'
};

const cardVirtualWidth = 200;

@Component({
    selector: 'app-employee-card-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss']
})
export class EmployeeCardFormComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: EmployeeCardService,
        private parameterService: ParameterService,
        private messageService: MessageService,
        private modal: NgbModal,
        private route: ActivatedRoute
    ) { }

    readonly form = new FormGroup({
        name: new FormControl(''),
        surname: new FormControl(''),
        structure: new FormControl('', [Validators.required]),
        position: new FormControl('', [Validators.required]),
        cardNumber: new FormControl('', [Validators.maxLength(10)]),
        chipCode: new FormControl('', [Validators.maxLength(10)])
    });

    id?: number;
    status?: EmployeeCardStatus;
    revocationReason?: string;
    name: string;
    surname: string;
    upn: string;
    position: string;
    structure: string;
    cardNumber: string;
    chipCode: string;
    cardProps: IEmployeeCardProps;
    staffError?: string;

    backImageUrl: string;
    frontImageUrl: string;

    photoImageUrl: string;
    photoHeight: number;
    photoWidth: number;
    photoHeightScaled: number;
    photoWidthScaled: number;

    minScale: number;
    maxScale: number;

    requirements: string;
    maxSize: number;
    accept: string;

    isAdmin: boolean;
    isPersonPickerOpened: boolean;
    isEdit: boolean;
    isFlipped: boolean;
    isStaffLoading: boolean;

    person?: {
        name: string,
        upn: string,
        displayName: string
    };

    readonly canPickPerson = this.app.currentUser.hasRight(codes.admin);

    readonly maxLength = {
        name: 0,
        surname: 0,
        position: 0,
        structure: 0,
        cardNumber: 10,
        chipCode: 10
    };

    readonly boundaryProps = {
        key: 0,
        left: 0,
        top: 0,
        height: 0,
        width: 0
    };

    private currentCard: IEmployeeCard;
    private photo: File;
    private templateChangesSubj: Subscription;
    private originalBackImageUrl: string;
    private originalFrontImageUrl: string;
    private aspectRatio: number;

    private photoDragging: {
        // mouse initial X
        x: number,
        // mouse initial Y
        y: number,
        // photo initial offset X
        offsetX: number,
        // photo initial offset Y
        offsetY: number,
        // boundary rectangle
        box: DOMRect,
        // photo image rectangle
        pic: DOMRect
    };

    private offsetX: number = 0;
    private offsetY: number = 0;

    @ViewChild('photoImg', { static: false }) private photoImg: ElementRef<HTMLImageElement>;
    @ViewChild('photoBox', { static: false }) private photoBox: ElementRef<HTMLDivElement>;

    get title(): string {
        return this.name && this.surname && this.upn ? `${this.name} ${this.surname} (${this.upn})` : '';
    }

    get isNew(): boolean {
        return !this.id;
    }

    get isValid(): boolean {
        return this.form.valid && (!this.isNew || (this.photo && this.photo.size <= this.maxSize));
    }

    get canEdit(): boolean {
        return this.status == 'Draft' || (this.status == 'Submitted' && this.isAdmin);
    }

    get canSubmit(): boolean {
        return this.status == 'Draft';
    }

    get canReview(): boolean {
        return this.status == 'Submitted' && this.isAdmin;
    }

    get canRevoke(): boolean {
        return this.status == 'Approved';
    }

    private _boxRect: DOMRect;
    private get boxRect(): DOMRect {
        if (!this._boxRect && this.photoBox) {
            this._boxRect = this.photoBox.nativeElement.getBoundingClientRect();
        }
        return this._boxRect;
    }

    ngOnInit() {
        this.isAdmin = this.app.currentUser.hasRight(codes.admin);

        if (this.isAdmin) {
            this.form.get('name').addValidators([Validators.required]);
            this.form.get('surname').addValidators([Validators.required]);
            this.form.get('cardNumber').addValidators([Validators.required]);
            this.form.get('chipCode').addValidators([Validators.required]);
        } else {
            this.form.get('name').disable();
            this.form.get('surname').disable();
            this.form.get('cardNumber').disable();
        }

        const id = +this.route.snapshot.params.id;

        if (id) {
            this.initCard(id);
        } else {
            this.status = 'Draft';

            if (this.canPickPerson) {
                this.togglePersonPicker();
            } else {
                this.edit(true);
            }
        }
    }

    ngOnDestroy() {
        this.templateChangesSubj?.unsubscribe();
    }

    togglePersonPicker(forceState?: boolean) {
        this.staffError = undefined;
        this.isPersonPickerOpened = forceState !== undefined ? forceState : !this.isPersonPickerOpened;
    }

    pickPerson(person: IPersonSearchResultItem) {
        const name = `${person.FirstName} ${person.LastName}`;
        const upn = person.Upn;

        this.person = {
            name,
            upn,
            displayName: `${name} (${upn})`
        };

        this.togglePersonPicker(false);
        this.edit(true);
    }

    flip() {
        this.isFlipped = !this.isFlipped;
    }

    onPhotoChange(event) {
        const file: File = event.target.files[0];
        const reader = new FileReader();

        reader.addEventListener('load', () => {
            this.setPhoto(reader.result as string, file);
            this.isFlipped = false;
        });

        if (file) {
            reader.readAsDataURL(file);
        } else {
            this.photoImageUrl = null;
        }
    }

    scale(width: number) {
        if (width > this.maxScale || width < this.minScale) return;

        this.photoWidthScaled = width;
        this.photoHeightScaled = Math.floor(this.photoHeight * (width / this.photoWidth));

        this.updatePhotoPosition();
    }

    edit(edit: boolean) {
        if (edit) {
            this.app.addLoading(forkJoin([
                this.parameterService.getValues(),
                this.messageService.getText(codes.requirements),
                this.service.getCardProps()
            ])).subscribe(([params, classifiers, cardProps]) => {
                this.maxSize = this.parameterService.findValue(params, 'EmployeeCardPhotoMaxSize', t => +t, 0);
                this.accept = this.parameterService.findValue(params, 'EmployeeCardPhotoExtensions',
                    t => '.' + t.split(',').join(',.'), '');
                this.maxLength.name = this.parameterService.findValue(params, 'EmployeeCardNameMaxLength', t => +t, 0);
                this.maxLength.surname = this.parameterService.findValue(params, 'EmployeeCardSurnameMaxLength', t => +t, 0);
                this.maxLength.position = this.parameterService.findValue(params, 'EmployeeCardPositionMaxLength', t => +t, 0);
                this.maxLength.structure = this.parameterService.findValue(params, 'EmployeeCardStructureMaxLength', t => +t, 0);

                this.form.get('name').addValidators([Validators.maxLength(this.maxLength.name)]);
                this.form.get('surname').addValidators([Validators.maxLength(this.maxLength.surname)]);
                this.form.get('position').addValidators([Validators.maxLength(this.maxLength.position)]);
                this.form.get('structure').addValidators([Validators.maxLength(this.maxLength.structure)]);

                this.requirements = classifiers.find(t => t.Code == codes.requirements)?.Text;

                if (this.isNew) {
                    this.setCardProps(cardProps);
                    this.loadDefaultFrontAndBack();
                    this.initPerson();
                } else {
                    this.setCardProps({
                        ...cardProps,
                        // keep current photo adjustments
                        PhotoOffsetX: this.cardProps.PhotoOffsetX,
                        PhotoOffsetY: this.cardProps.PhotoOffsetY,
                        PhotoScale: this.cardProps.PhotoScale
                    });

                    this.form.setValue({
                        name: this.name || '',
                        surname: this.surname || '',
                        position: this.position || '',
                        structure: this.structure || '',
                        cardNumber: this.cardNumber || '',
                        chipCode: this.chipCode || ''
                    });

                    this.service.getPhotoDataUrl(this.id).subscribe(url => {
                        this.setPhoto(url, null, {
                            scale: this.cardProps.PhotoScale,
                            offsetX: this.cardProps.PhotoOffsetX,
                            offsetY: this.cardProps.PhotoOffsetY
                        });
                        this.loadDefaultFrontAndBack();
                    });
                }

                this.isEdit = true;
            });
        } else {
            this.photo = undefined;
            this.photoImageUrl = undefined;

            this.frontImageUrl = this.originalFrontImageUrl;
            this.backImageUrl = this.originalBackImageUrl;

            if (this.isNew) {
                this.goToList();
            } else {
                this.isEdit = false;
            }
        }
    }

    save(submit: boolean = false) {
        const isNew = this.isNew;

        if (submit) {
            this.createOrUpdate().subscribe(() => {
                this.submit();
            });
        } else {
            this.createOrUpdate().subscribe(() => {
                this.app.notify(this.app.translate('employeeCard_saved'));

                if (isNew) {
                    this.app.navigate(['/employee-cards', this.id]);
                } else {
                    this.initCard(this.id, () => {
                        this.edit(false);
                    });
                }
            });
        }
    }

    submit() {
        this.app.addLoading(this.service.submit(this.id)).subscribe(() => {
            this.app.notify(this.app.translate('employeeCard_submitted'));
            this.goToList();
        });
    }

    revoke() {
        const ref = this.modal.open(EmployeeCardRevokeComponent);
        ref.componentInstance.card = this.currentCard;
        ref.result.then(() => {
            this.goToList();
        }, () => { });
    }

    approve() {
        const isEdit = this.isEdit;
        const doApprove = () => {
            this.app.addLoading(this.service.approve(this.id)).subscribe(() => {
                this.app.notify(this.app.translate('employeeCard_approved'));
                this.goToList();
            });
        };

        this.app.confirm(this.app.translate('employeeCard_confirmApprove'), ok => {
            if (!ok) return;

            if (isEdit) {
                this.createOrUpdate().subscribe(() => {
                    doApprove();
                });
            } else {
                doApprove();
            }
        });
    }

    reject() {
        this.app.confirm(this.app.translate('employeeCard_confirmReject'), ok => {
            if (!ok) return;
            this.app.addLoading(this.service.reject(this.id)).subscribe(() => {
                this.app.notify(this.app.translate('employeeCard_rejected'));
                this.goToList();
            });
        });
    }

    close() {
        this.goToList();
    }

    onWheel(event: WheelEvent) {
        event.preventDefault();
        event.stopPropagation();

        this.scale(this.photoWidthScaled - event.deltaY);
    }

    onDragStart(event: MouseEvent) {
        event.preventDefault();

        this.photoDragging = {
            x: event.clientX,
            y: event.clientY,
            offsetX: this.offsetX,
            offsetY: this.offsetY,
            pic: this.photoImg.nativeElement.getBoundingClientRect(),
            box: this.boxRect
        };
    }

    @HostListener('document:mousemove', ['$event'])
    onDragMove(event: MouseEvent) {
        if (!this.photoDragging) return;

        const travelX = event.clientX - this.photoDragging.x;
        const travelY = event.clientY - this.photoDragging.y;

        let offsetX = this.photoDragging.offsetX + travelX;
        let offsetY = this.photoDragging.offsetY + travelY;

        this.updatePhotoPosition({ x: offsetX, y: offsetY });
    }

    @HostListener('document:mouseup', ['$event'])
    @HostListener('document:mouseleave', ['$event'])
    onDragEnd() {
        this.photoDragging = undefined;
    }

    private updatePhotoPosition(offset?: { x: number, y: number }) {
        if (!this.photoImg) return;

        const box = this.boxRect;

        if (!box) return;

        let offsetX = offset?.x || this.offsetX;
        let offsetY = offset?.y || this.offsetY;

        const pic = {
            left: box.left + offsetX,
            right: box.left + offsetX + this.photoWidthScaled,
            top: box.top + offsetY,
            bottom: box.top + offsetY + this.photoHeightScaled
        };

        if (pic.left > box.left) offsetX = 0;
        else if (pic.right < box.right) offsetX += (box.right - pic.right);

        if (pic.top > box.top) offsetY = 0;
        else if (pic.bottom < box.bottom) offsetY += (box.bottom - pic.bottom);

        this.offsetX = offsetX;
        this.offsetY = offsetY;

        this.photoImg.nativeElement.style.transform = `translate3d(${offsetX}px, ${offsetY}px, 0)`;
    }

    private goToList() {
        this.app.navigate(['/employee-cards'], { queryParams: { noRedirect: true } });
    }

    private setPhoto(dataUrl: string, file?: File, options?: { scale: number, offsetX: number, offsetY: number }) {
        Utils.urlToImage(dataUrl).then(img => {
            this.photo = file;
            this.photoHeight = img.naturalHeight;
            this.photoWidth = img.naturalWidth;

            const minWidth = this.cardProps.AvatarWidth;
            const minRatio = minWidth / this.photoWidth;
            const minHeight = this.photoHeight * minRatio;

            this.minScale = minHeight < this.cardProps.AvatarHeight ? Math.floor(minWidth / (minHeight / this.cardProps.AvatarHeight)) : minWidth;
            this.maxScale = this.photoWidth < this.minScale ? this.minScale : this.photoWidth;

            const minScale = this.minScale;

            this.photoImageUrl = dataUrl;

            if (options?.scale) {
                const width = Math.floor(options.scale * this.maxScale);

                if (width >= minScale && width <= this.maxScale) {
                    this.scale(width);
                } else {
                    this.scale(minScale);
                }
            } else {
                this.scale(minScale);
            }

            // wait a bit for an img element to appear after receiving photo URL
            setTimeout(() => {
                this.updatePhotoPosition({ x: options?.offsetX || 0, y: options?.offsetY || 0 });
            });
        });
    }

    private initPerson() {
        this.isStaffLoading = true;
        this.app.addLoading(this.service.getStaff(this.person?.upn)).pipe(finalize(() => {
            this.isStaffLoading = false;
        })).subscribe(data => {
            if (data) {
                this.form.get('name').setValue(data.Name);
                this.form.get('surname').setValue(data.Surname);
                this.form.get('position').setValue(data.Position);
                this.form.get('structure').setValue(data.Structure);
                this.form.get('cardNumber').setValue(data.CardNumber);

                this.upn = data.Upn;

                if (data.PhotoBase64) {
                    const photoDataUrl = `data:image/jpeg;base64,${data.PhotoBase64}`;
                    this.setPhoto(photoDataUrl, Utils.dataUrlToFile(photoDataUrl, `${data.CardNumber}.jpg`));
                }
            } else {
                this.staffError = this.app.translate('employeeCard_staffNotFound');
            }
        }, err => {
            this.staffError = this.app.translate('employeeCard_staffNotFound');
            throw err;
        });
    }

    private initCard(id: number, callback?: () => void) {
        this.id = id;

        this.app.addLoading(forkJoin([
            this.service.getById(id),
            this.service.getFrontDataUrl(id),
            this.service.getBackDataUrl(id)
        ])).subscribe(d => {
            const [data, frontUrl, backUrl] = d;

            this.currentCard = data;
            this.name = data.Name;
            this.surname = data.Surname;
            this.upn = data.Upn;
            this.position = data.Position;
            this.structure = data.Structure;
            this.cardNumber = data.CardNumber;
            this.chipCode = data.ChipCode;
            this.status = data.Status;
            this.revocationReason = data.RevocationReason;

            this.setCardProps(data.CardProps);

            this.frontImageUrl = this.originalFrontImageUrl = frontUrl;
            this.backImageUrl = this.originalBackImageUrl = backUrl;

            if (callback) callback();
        });
    }

    private createOrUpdate() {
        const scale = this.photoWidthScaled / this.photoWidth;

        const model: IEmployeeCardEditModel = {
            Name: this.form.get('name').value,
            Surname: this.form.get('surname').value,
            Position: this.form.get('position').value,
            Structure: this.form.get('structure').value,
            CardNumber: this.form.get('cardNumber').value,
            ChipCode: this.form.get('chipCode').value,
            Photo: this.photo,
            PhotoProps: {
                OffsetX: this.offsetX,
                OffsetY: this.offsetY,
                Scale: scale,
                AspectRatio: this.aspectRatio
            }
        };

        const req = this.isNew
            ? this.service.create(model, this.person?.upn).pipe(tap(id => {
                this.id = id;
            }))
            : this.service.update(this.id, model);

        return this.app.addLoading(req);
    }

    private loadDefaultFrontAndBack() {
        this.service.getBackTemplateDataUrl().subscribe(dataUrl => {
            this.backImageUrl = dataUrl;
        });

        this.service.getFrontTemplateDataUrl().subscribe(dataUrl => {
            this.frontImageUrl = dataUrl;
        });
    }

    private setCardProps(props: IEmployeeCardProps) {
        const ogWidth = props.AvatarWidth;
        const cardSizeRatio = cardVirtualWidth / props.Width;

        this.cardProps = {
            Width: Math.floor(cardVirtualWidth),
            Height: Math.floor(props.Height * cardSizeRatio),
            AvatarHeight: Math.floor(props.AvatarHeight * cardSizeRatio),
            AvatarWidth: Math.floor(props.AvatarWidth * cardSizeRatio),
            AvatarLeft: Math.floor(props.AvatarLeft * cardSizeRatio),
            AvatarTop: Math.floor(props.AvatarTop * cardSizeRatio),
            PhotoOffsetX: props.PhotoOffsetX,
            PhotoOffsetY: props.PhotoOffsetY,
            PhotoScale: props.PhotoScale
        };

        this.aspectRatio = ogWidth / this.cardProps.AvatarWidth;
    }
}
